import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { ReportTable, Caveats, AdminReportGate, reportGet } from "@/components/reports/report-primitives";
import { PercentBarChart } from "@/components/reports/report-charts";

export default function DocumentationReportPage() {
  usePageTitle("Documentation & staff");
  const health = useQuery({ queryKey: ["/api/reports/documentation-health"], queryFn: () => reportGet("/api/reports/documentation-health") });
  const base = useQuery({ queryKey: ["/api/reports/note-coverage"], queryFn: () => reportGet("/api/reports/note-coverage") });

  const rows: any[] = health.data?.rows ?? [];
  const baseRows: any[] = base.data?.rows ?? [];
  const nonLetter = baseRows.find(r => r.kind === "non-letter work");

  return (
    <AdminReportGate>
    <ResponsiveLayout title="Documentation & staff" subtitle="Evidence, not a score"
      breadcrumbs={[{ label: "Reports", href: "/reports" }, { label: "Documentation & staff" }]}
      backTo={{ label: "All reports", href: "/reports" }}>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          These are <b>observations, not rankings</b>. Dottie records no reviewer, QA outcome,
          complaint or time spent, so there is nothing here to grade against. Owner is who holds the
          order now — Dottie keeps no assignment history. The base rate is <b>open work only</b>, so
          it is comparable to the per-owner figures.
        </div>

        <h2 className="text-xl font-medium mt-10 mb-1">The base rate</h2>
        <p className="text-sm text-gray-500 mb-4 max-w-3xl">
          Read the per-owner figures against this. If non-letter work is largely undocumented across
          the board, an individual’s unnoted orders are unremarkable and the finding is organisational.
        </p>
        {baseRows.length > 0 && (
          <ReportTable
            columns={[
              { key: "kind", header: "Work kind" },
              { key: "workOrders", header: "Work orders", align: "right" },
              { key: "withANote", header: "With a note", align: "right" },
              { key: "pctNoted", header: "% noted", align: "right", render: (r: any) => `${r.pctNoted}%` },
            ]}
            rows={baseRows}
          />
        )}

        <h2 className="text-xl font-medium mt-12 mb-1">Note coverage by owner</h2>
        <p className="text-sm text-gray-500 mb-4">
          Open non-letter work only.
          {nonLetter && <> Base rate for comparison: <b>{nonLetter.pctNoted}%</b>.</>}
        </p>
        <PercentBarChart label="Noted recently"
          rows={rows.map(r => ({
            label: r.assignee ?? "Unassigned",
            value: Math.round(r.notedShare * 100),
            sub: `${r.openOrders} open · ${r.neverNoted} never noted`,
          }))}
        />

        <div className="mt-8">
          <ReportTable
            columns={[
              { key: "assignee", header: "Owner", render: (r: any) => r.assignee ?? "Unassigned" },
              { key: "openOrders", header: "Open orders", align: "right" },
              { key: "notedRecently", header: "Noted recently", align: "right" },
              { key: "stale", header: "Stale", align: "right" },
              { key: "neverNoted", header: "Never noted", align: "right" },
              { key: "notedShare", header: "Share noted", align: "right",
                render: (r: any) => `${Math.round(r.notedShare * 100)}%` },
            ]}
            rows={rows}
          />
        </div>

        <div className="mt-6">
          <a href="/api/reports/documentation-health.csv" className="text-sm text-indigo-600 hover:underline">Download CSV</a>
        </div>
        <Caveats items={[...(health.data?.meta?.caveats ?? []), ...(base.data?.meta?.caveats ?? [])]} />
    </ResponsiveLayout>
    </AdminReportGate>
  );
}
